<template>
  <div
    class="task-due-date-cell d-flex align-center"
    :class="{
      'task-due-date-cell--days-only': daysOnly,
      'task-due-date-cell--with-pill': withPill,
    }"
    :data-testid="dataTestId"
  >
    <span
      v-if="!daysOnly"
      class="task-due-date-cell__date"
      data-testid="tasklist__due-date-label"
    >
      {{ formattedDate }}
    </span>

    <!-- Due today: days-only uses icon + 0 (same format as overdue/remaining). -->
    <v-tooltip
      v-if="display.state === dueDateStates.DUE_TODAY && daysOnly"
      bottom
      content-class="tooltip-bottom"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="task-due-date-cell__today"
          v-bind="attrs"
          v-on="on"
          data-testid="tasklist__due-date-today-badge"
        >
          <img
            src="/img/icons/fbc-icons/DueDateToday.svg"
            alt=""
            class="task-due-date-cell__today-icon-image"
          >
          <span class="task-due-date-cell__today-count">0</span>
        </span>
      </template>
      <span>Due Today</span>
    </v-tooltip>

    <!-- Due today: task-list (with date) keeps icon-only badge. -->
    <v-tooltip
      v-else-if="display.state === dueDateStates.DUE_TODAY"
      bottom
      content-class="tooltip-bottom"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="task-due-date-cell__badge task-due-date-cell__badge--today"
          v-bind="attrs"
          v-on="on"
          data-testid="tasklist__due-date-today-badge"
        >
          <img src="/img/icons/fbc-icons/DueDateToday.svg" alt="Due Today" class="task-due-date-cell__today-icon-image">
        </span>
      </template>
      <span>Due Today</span>
    </v-tooltip>

    <v-tooltip
      v-else-if="display.state === dueDateStates.OVERDUE"
      bottom
      content-class="tooltip-bottom"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="task-due-date-cell__overdue"
          v-bind="attrs"
          v-on="on"
          data-testid="tasklist__due-date-overdue-badge"
        >
          <span class="task-due-date-cell__overdue-icon">
            <img
              src="/img/icons/fbc-icons/DueDateIcon.svg"
              alt=""
              class="task-due-date-cell__overdue-icon-image"
            >
          </span>
          <span class="task-due-date-cell__overdue-count">{{ display.badgeLabel }}</span>
        </span>
      </template>
      <span>Days Overdue</span>
    </v-tooltip>

    <v-tooltip
      v-else-if="daysOnly && remainingDays != null"
      bottom
      content-class="tooltip-bottom"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          class="task-due-date-cell__remaining"
          v-bind="attrs"
          v-on="on"
          data-testid="tasklist__due-date-remaining-badge"
        >
          <v-icon size="12" class="task-due-date-cell__remaining-icon">mdi-clock-outline</v-icon>
          <span class="task-due-date-cell__remaining-count">{{ remainingDays }}</span>
        </span>
      </template>
      <span>Days Remaining</span>
    </v-tooltip>
  </div>
</template>

<script>
import './TaskDueDateCell.scss';
import {
  DUE_DATE_DISPLAY_STATE,
  resolveDueDateCellDisplay,
  toDateOnly,
} from '../../../../../utils/tasklistHelpers';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export default {
  name: 'TaskDueDateCell',

  props: {
    dueDate: {
      type: [String, Date],
      default: '',
    },
    dueDays: {
      type: Number,
      default: null,
    },
    referenceDate: {
      type: [String, Date],
      default: () => new Date(),
    },
    /** When true, hide the formatted date and show days badge only (IM Due Days column). */
    daysOnly: {
      type: Boolean,
      default: false,
    },
    /** When true, show pill backgrounds on overdue/remaining/today badges (e.g. request detail). */
    withPill: {
      type: Boolean,
      default: false,
    },
    /** When true, hide Due Today / Overdue urgency (e.g. approved/closed tasks). */
    suppressUrgency: {
      type: Boolean,
      default: false,
    },
    dataTestId: {
      type: String,
      default: 'task-due-date-cell',
    },
  },

  computed: {
    dueDateStates() {
      return DUE_DATE_DISPLAY_STATE;
    },

    display() {
      return resolveDueDateCellDisplay(this.dueDate, this.dueDays, this.referenceDate, {
        suppressUrgency: this.suppressUrgency,
      });
    },

    formattedDate() {
      if (!this.dueDate) return '—';
      return this.$nova.formatDate(this.dueDate, 'DD MMM YYYY');
    },

    remainingDays() {
      if (this.display.state !== DUE_DATE_DISPLAY_STATE.NOT_YET_DUE) return null;
      if (typeof this.dueDays === 'number' && Number.isFinite(this.dueDays) && this.dueDays > 0) {
        return this.dueDays;
      }
      const due = toDateOnly(this.dueDate);
      const today = toDateOnly(this.referenceDate);
      if (!due || !today) return null;
      const days = Math.round((due.getTime() - today.getTime()) / MS_PER_DAY);
      return days > 0 ? days : null;
    },
  },
};
</script>
